/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ImageUrlGenerator } from "@tivocorp/data-domain-lib";
import { FeedItemType } from "client-code/smart-search";
import type { Carousel, FeedItem, FeedItems } from "client-code/smart-search";

import { CarouselTemplate, OfferType } from "../../model/enums";
import type {
  TCarouselItem,
  TCarouselRail,
  TContentType
} from "../../model/types";
import { getTilePixelDimensions } from "./CarouselTemplateUtil";

const imageUrlGenerator: ImageUrlGenerator = new ImageUrlGenerator();

const DEFAULT_SEARCH_TEMPLATE: CarouselTemplate =
  CarouselTemplate.POSTER_HORIZONTAL;

const mapFeedItemTypeToContentType = (
  feedItemType: FeedItemType
): TContentType => {
  switch (feedItemType) {
    case FeedItemType.Episode:
      return "episode";
    case FeedItemType.Movie:
      return "movie";
    case FeedItemType.Series:
      return "series";
    case FeedItemType.SportsEvent:
      return "sports";
    case FeedItemType.SportsOrganization:
      return "leagueOrganization";
    case FeedItemType.SportsTeam:
      return "team";
    case FeedItemType.JumpChannel:
    case FeedItemType.LinearProvider:
      return "channel";
    case FeedItemType.StreamingProvider:
      return "ottApplication";
    case FeedItemType.Person:
      return "person";
    default:
      return "channel";
  }
};

const resolveCarouselTemplate = (carousel: Carousel): CarouselTemplate =>
  carousel.template !== undefined && carousel.template !== null
    ? (carousel.template as CarouselTemplate)
    : DEFAULT_SEARCH_TEMPLATE;

const isFeedItemsWrapper = (entry: FeedItem | FeedItems): entry is FeedItems =>
  Array.isArray((entry as FeedItems).feedItems);

const flattenCarouselFeedItems = (carousel: Carousel): FeedItem[] => {
  const entries = (carousel.feedItems || []) as Array<FeedItem | FeedItems>;
  const items: FeedItem[] = [];
  for (const entry of entries) {
    const wrapperItems: FeedItem[] = isFeedItemsWrapper(entry)
      ? entry.feedItems || []
      : [entry];
    for (const item of wrapperItems) {
      if (item.feedItemType !== FeedItemType.Promotion) {
        items.push(item);
      }
    }
  }
  return items;
};

/** Maps a Smart Search item into the common carousel item used by views. */
export const mapSmartSearchFeedItem = (
  feedItem: FeedItem,
  imageHostBaseUrl: string,
  template: CarouselTemplate,
  categoryLabel: string
): TCarouselItem => {
  const isAdult: boolean = feedItem.isAdult || false;
  const isLive: boolean = feedItem.isLive || false;
  const duration: number = feedItem.durationSecs || 0;
  const imageType = imageUrlGenerator.getImageTypeFromFeedItemType(
    feedItem.feedItemType as Parameters<
      typeof imageUrlGenerator.getImageTypeFromFeedItemType
    >[0]
  );
  const tileDimensions = getTilePixelDimensions(template);
  const imageUrl: string =
    imageHostBaseUrl.length > 0
      ? imageUrlGenerator.buildImageUrl(
          imageHostBaseUrl,
          feedItem.digItemId || "",
          imageType,
          tileDimensions.width,
          tileDimensions.height
        )
      : "";

  return {
    id: feedItem.feedItemId,
    feedItemType: feedItem.feedItemType,
    title: feedItem.title,
    description: feedItem.caption || "",
    imageUrl,
    contentType: mapFeedItemTypeToContentType(feedItem.feedItemType),
    categoryLabel,
    duration,
    isLive,
    isAdult,
    offerType: feedItem.offerType as OfferType | undefined,
    startTime: feedItem.startTime,
    endTime: feedItem.endTime,
    stationId: feedItem.stationId,
    seasonNumber: feedItem.seasonNum,
    episodeNumber: feedItem.episodeNum,
    episodeTitle: feedItem.eventTitle,
    ratingBadge: feedItem.tivoTvRating,
    isNew: feedItem.isNew,
    progressPercent: feedItem.percentWatched,
    movieYear: feedItem.movieYear
  };
};

/** Maps one Smart Search response carousel into a reusable application rail. */
export const mapSmartSearchCarouselToRail = (
  carousel: Carousel,
  imageHostBaseUrl: string
): TCarouselRail => {
  const template: CarouselTemplate = resolveCarouselTemplate(carousel);
  const feedItems: FeedItem[] = flattenCarouselFeedItems(carousel);
  const pageCursor: string | null =
    carousel.pageCursor !== undefined &&
    carousel.pageCursor !== null &&
    carousel.pageCursor.length > 0
      ? carousel.pageCursor
      : null;

  return {
    id: carousel.carouselName,
    title: carousel.caption,
    template,
    items: feedItems.map(
      (item: FeedItem): TCarouselItem =>
        mapSmartSearchFeedItem(
          item,
          imageHostBaseUrl,
          template,
          carousel.caption
        )
    ),
    pageCursor
  };
};

/** Whether a complete rail consists only of channel or provider logo items. */
export const isSmartSearchChannelRail = (rail: TCarouselRail): boolean =>
  rail.items.length > 0 &&
  rail.items.every(
    (item: TCarouselItem): boolean =>
      item.contentType === "channel" || item.contentType === "ottApplication"
  );
